import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaCopy, FaSpinner } from 'react-icons/fa';
import '../dashboard/css/DashboardManager.css';

/**
 * Prompt for label + API name before cloning a dashboard or home layout.
 */
const CloneNameModal = ({
  show,
  entityType = 'record',
  labelCaption = 'Label',
  nameCaption = 'Name',
  hint = null,
  sourceLabel = '',
  defaultLabel = '',
  defaultName = '',
  deriveNameFromLabel,
  saving = false,
  error = null,
  onClose,
  onConfirm,
}) => {
  const [label, setLabel] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    if (show) {
      setLabel(defaultLabel || '');
      setName(defaultName || '');
    }
  }, [show, defaultLabel, defaultName]);

  const handleLabelChange = (value) => {
    setLabel(value);
    if (typeof deriveNameFromLabel === 'function') {
      setName(deriveNameFromLabel(value));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedLabel = label.trim();
    const trimmedName = name.trim();
    if (!trimmedLabel) return;
    onConfirm?.({
      label: trimmedLabel,
      name: trimmedName || (deriveNameFromLabel ? deriveNameFromLabel(trimmedLabel) : ''),
    });
  };

  if (!show) return null;

  const modalContent = (
    <div className="entity-assign-modal-scope dashboard-manager">
      <div
        className="dashboard-modal-overlay"
        role="presentation"
        onClick={saving ? undefined : onClose}
      >
        <div
          className="dashboard-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="clone-modal-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="dashboard-modal-header">
            <h2 id="clone-modal-title">
              <FaCopy className="dashboard-modal-title-icon" /> Clone {entityType}
            </h2>
            <button
              type="button"
              className="dashboard-close-btn"
              onClick={onClose}
              disabled={saving}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="dashboard-modal-form">
            <p className="dashboard-form-hint dashboard-assign-intro">
              Cloning <strong>{sourceLabel || entityType}</strong>. Enter a label and unique
              name for the new copy.
              {hint ? ` ${hint}` : ' Widgets and layout settings will be copied.'}
            </p>

            {error && (
              <div className="dashboard-form-error" role="alert">
                {error}
              </div>
            )}

            <div className="dashboard-form-group">
              <label htmlFor="clone-label">{labelCaption} *</label>
              <input
                id="clone-label"
                type="text"
                value={label}
                onChange={(e) => handleLabelChange(e.target.value)}
                required
                autoFocus
                disabled={saving}
                placeholder="Display name for the copy"
              />
            </div>

            <div className="dashboard-form-group">
              <label htmlFor="clone-name">{nameCaption} *</label>
              <input
                id="clone-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={saving}
                placeholder="Unique API name (lowercase, underscores)"
              />
              <p className="dashboard-form-hint text-muted small mt-1 mb-0">
                Must be unique. Auto-filled from the label; you can edit it.
              </p>
            </div>

            <div className="dashboard-modal-actions">
              <button
                type="button"
                className="dashboard-cancel-btn"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="dashboard-save-btn"
                disabled={saving || !label.trim() || !name.trim()}
              >
                {saving ? (
                  <>
                    <FaSpinner className="fa-spin me-1" /> Cloning…
                  </>
                ) : (
                  'Clone'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CloneNameModal;
